import { useState } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authService } from '../services/api';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';

function RegisterPage() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [password2, setPassword2] = useState('');
    const [language, setLanguage] = useState(
        localStorage.getItem('language') === 'fr' ? 'fr' : 'en'
    );
    const [agreed, setAgreed] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { t, i18n } = useTranslation();
    const { googleLogin } = useAuth();

    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();

        const cleanEmail = email.trim().toLowerCase();
        const cleanPassword = password.trim();
        const cleanPassword2 = password2.trim();

        if (!cleanEmail || !cleanPassword || !cleanPassword2) {
            setError(t('auth.errors.all_fields_required'));
            return;
        }

        if (cleanPassword !== cleanPassword2) {
            setError(t('auth.errors.passwords_no_match'));
            return;
        }

        if (cleanPassword.length < 8) {
            setError(t('auth.errors.password_min_length'));
            return;
        }

        if (!agreed) {
            setError(t('auth.errors.terms_required'));
            return;
        }

        setLoading(true);
        setError('');

        try {
            await authService.register(cleanEmail, cleanPassword, cleanPassword2, language);
            i18n.changeLanguage(language);
            localStorage.setItem('language', language);
            navigate('/login');
        } catch (err) {
            setError(t('auth.errors.registration_failed'));
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleSuccess = async (credentialResponse) => {
        if (!credentialResponse?.credential) {
            setError('Google sign up failed. Try again.');
            return;
        }

        setLoading(true);
        setError('');

        try {
            await googleLogin(credentialResponse.credential, language);
            navigate('/dashboard');
        } catch (err) {
            const backendError = err?.response?.data?.error;
            setError(backendError || 'Google sign up failed. Try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="auth-container">
            <SEO
                title="Create Account"
                description="Create a free Career Platform account. Get AI-powered resume analysis, interview simulation, job search, and career coaching."
                path="/register"
            />
            <div className="auth-card">
                <h1>{t('app.name')}</h1>
                <h2>{t('auth.register')}</h2>

                {error && <div className="error-message">{error}</div>}

                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label>{t('auth.email')}</label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder={t('auth.placeholders.email')}
                            required
                            autoComplete="email"
                        />
                    </div>

                    <div className="form-group">
                        <label>{t('auth.password')}</label>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder={t('auth.placeholders.password_min')}
                            required
                            autoComplete="new-password"
                        />
                    </div>

                    <div className="form-group">
                        <label>{t('auth.confirm_password')}</label>
                        <input
                            type="password"
                            value={password2}
                            onChange={(e) => setPassword2(e.target.value)}
                            placeholder={t('auth.placeholders.password_repeat')}
                            required
                            autoComplete="new-password"
                        />
                    </div>

                    <div className="form-group">
                        <label>{t('settings.language')}</label>
                        <select
                            value={language}
                            onChange={(e) => {
                                const selected = e.target.value;
                                setLanguage(selected);
                                i18n.changeLanguage(selected);
                            }}
                        >
                            <option value="en">{t('settings.english')}</option>
                            <option value="fr">{t('settings.french')}</option>
                        </select>
                    </div>

                    {/* Terms and Conditions checkbox */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '0.6rem',
                        margin: '1rem 0',
                        padding: '0.75rem',
                        background: agreed ? '#f0fdf4' : '#f8fafc',
                        border: `1px solid ${agreed ? '#bbf7d0' : '#e2e8f0'}`,
                        borderRadius: '8px',
                        transition: 'all 0.2s',
                    }}>
                        <input
                            type="checkbox"
                            id="terms"
                            checked={agreed}
                            onChange={(e) => setAgreed(e.target.checked)}
                            style={{
                                width: '16px',
                                height: '16px',
                                marginTop: '2px',
                                cursor: 'pointer',
                                flexShrink: 0,
                            }}
                        />
                        <label
                            htmlFor="terms"
                            style={{
                                fontSize: '0.85rem',
                                color: '#475569',
                                cursor: 'pointer',
                                lineHeight: '1.5',
                            }}
                        >
                            {t('auth.terms_agree')}{' '}
                            <Link
                                to="/terms"
                                target="_blank"
                                style={{ color: '#3b82f6', fontWeight: '600' }}
                            >
                                {t('auth.terms_link')}
                            </Link>
                            {' '}{t('auth.terms_age')}
                        </label>
                    </div>

                    <button
                        type="submit"
                        disabled={loading || !agreed}
                        style={{
                            opacity: !agreed ? 0.6 : 1,
                            cursor: !agreed ? 'not-allowed' : 'pointer',
                        }}
                    >
                        {loading ? t('auth.creating') : t('auth.create_account')}
                    </button>
                </form>

                <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'center' }}>
                    <GoogleLogin
                        onSuccess={handleGoogleSuccess}
                        onError={() => setError('Google sign up failed. Try again.')}
                    />
                </div>

                <p className="auth-link">
                    {t('auth.have_account')} <Link to="/login">{t('auth.sign_in')}</Link>
                </p>
            </div>
        </div>
    );
}

export default RegisterPage;